import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { ArchivedDocumentWithDetails, Claim, ClaimAdminReviewRequest, PreferredResolutionType } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGetDocumentsFromString } from "@/services/Documents";
import { putReviewClaim } from "@/services/Claims";

import Text from "@/components/atoms/Text";
import Space from "@/components/atoms/Space";
import Divider from "@/components/atoms/Divider";
import Textarea from "@/components/atoms/Textarea";
import Stack from "@/components/atoms/Stack";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import styles from "./styles.module.css";
import ImageCarousel from "@/components/molecules/ImageCarousel";
import { useAuth } from "@/contexts/AuthContext";
import Loader from "@/components/atoms/Loader";
import ClaimStatusEnum from "@/constants/enums/ClaimStatusEnum";
import Select from "@/components/atoms/Select";
import ClaimPreferredResolutionEnum from "@/constants/enums/ClaimPreferredResolutionEnum";

interface DetailsTabProps {
  claim: Claim | null;
  onClose: () => void;
}

const DetailsTab = ({ claim, onClose }: DetailsTabProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentCompany } = useAuth();
  const [adminNote, setAdminNote] = useState<string>("");
  const [selectedSolution, setSelectedSolution] = useState<PreferredResolutionType | null>(null);
  const refundPercentOptions = ["10%", "20%", "30%", "40%", "50%", "60%", "70%", "80%", "90%", "100%"];
  // const [refundPercentOptions, setRefundPercentOptions] = useState([]);
  const [selectedRefundPercent, setSelectedRefundPercent] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const documents = useGetDocumentsFromString(claim?.attachments ?? [], currentCompany?.companyId);

  useEffect(() => {
    if (claim) {
      setAdminNote(claim.adminResolutionNote ?? "");
      setSelectedSolution(claim.proposals?.[0] ?? null);
      setErrors({});
    }
  }, [claim]);

  const { mutateAsync: mutateReviewClaim, isPending: isPendingReviewClaim } = useMutation({
    mutationFn: async (claim: ClaimAdminReviewRequest) => {
      return await putReviewClaim(claim);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getClaimsAll"] });
      onClose();
    },
  });

  const handleApproveClaim = async () => {
    if (!adminNote) {
      setErrors({
        ...errors,
        adminNote: "required",
      });
    }
    if (!selectedSolution) {
      setErrors({
        ...errors,
        preferredResolution: "required",
      });
    }
    if (selectedSolution === ClaimPreferredResolutionEnum.REFUND_PARTIAL && !selectedRefundPercent) {
      setErrors({
        ...errors,
        refundPercent: "required",
      });
    }
    if (!adminNote || !selectedSolution) {
      return;
    }

    if (!claim) return;
    const claimReview: ClaimAdminReviewRequest = {
      claimId: claim.id,
      newStatus: ClaimStatusEnum.ACCEPTED,
      adminResolutionNote: adminNote,
      approvedProposal: [selectedSolution],
      refundInstructions: "",
      creditPercent:
        selectedSolution === ClaimPreferredResolutionEnum.REFUND_PARTIAL ? parseInt(selectedRefundPercent) : null,
    };
    await mutateReviewClaim(claimReview);
  };

  const handleRejectClaim = async () => {
    if (!adminNote) {
      setErrors({
        adminNote: "required",
      });
      return;
    }

    if (!claim) return;
    const claimReview: ClaimAdminReviewRequest = {
      claimId: claim.id,
      newStatus: ClaimStatusEnum.REJECTED,
      adminResolutionNote: adminNote,
      approvedProposal: [],
      refundInstructions: "",
      creditPercent: null,
    };
    await mutateReviewClaim(claimReview);
  };

  const PreferredResolution: {
    value: PreferredResolutionType;
    label: string;
  }[] = [
    {
      value: ClaimPreferredResolutionEnum.REFUND_FULL,
      label: t("enumerables:preferredSolutions.REFUND_FULL"),
    },
    {
      value: ClaimPreferredResolutionEnum.REFUND_PARTIAL,
      label: t("enumerables:preferredSolutions.REFUND_PARTIAL"),
    },
    {
      value: ClaimPreferredResolutionEnum.REPLACEMENT,
      label: t("enumerables:preferredSolutions.REPLACEMENT"),
    },
    {
      value: ClaimPreferredResolutionEnum.RETURN_ITEM,
      label: t("enumerables:preferredSolutions.RETURN_ITEM"),
    },
  ];

  const isDisabled = !(claim?.status === ClaimStatusEnum.UNDER_REVIEW || claim?.status === ClaimStatusEnum.SUBMITTED);

  return (
    <>
      <Grid className={styles.root}>
        <Grid.Col span={4}>
          <Text size="sm" color="secondary">
            {t("claims:issuer")}
          </Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <Text size="sm">{claim?.createdByCompany.companyName}</Text>
        </Grid.Col>

        <Grid.Col span={4}>
          <Text size="sm" color="secondary">
            {t("claims:counterparty")}
          </Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <Text size="sm">{claim?.counterPartyCompany.companyName}</Text>
        </Grid.Col>

        <Grid.Col span={4}>
          <Text size="sm" color="secondary">
            {t("claims:issue")}
          </Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <Text size="sm">
            {claim?.problemTypes.map((problem) => t("enumerables:problemTypes." + problem)).join(", ")}
          </Text>
        </Grid.Col>

        <Grid.Col span={4}>
          <Text size="sm" color="secondary">
            {t("claims:preferredSolution")}
          </Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <Text size="sm">{t("enumerables:preferredSolutions." + claim?.preferredResolution)}</Text>
        </Grid.Col>

        <Grid.Col span={4}>
          <Text size="sm" color="secondary">
            {t("claims:comment")}
          </Text>
        </Grid.Col>
        <Grid.Col span={8}>
          <Text size="sm" color="tertiary">
            {claim?.customerNote}
          </Text>
        </Grid.Col>

        <Grid.Col span={4}>
          <Text size="sm" color="secondary">
            {t("common:images")}
          </Text>
        </Grid.Col>
        <Grid.Col span={8}>
          {documents.pending && <Loader />}
          {!documents.pending && documents && (
            <ImageCarousel
              images={documents.images.map((doc) => doc) as ArchivedDocumentWithDetails[]}
              height={80}
              imagesPrSlide={3}
            />
          )}
        </Grid.Col>
      </Grid>

      <Divider />
      <Space height="37px" />

      <Grid className={styles.root}>
        <Grid.Col offset={4} span={6}>
          <Stack gap="20px">
            {selectedSolution !== ClaimPreferredResolutionEnum.REFUND_PARTIAL && (
              <Select
                label={t("claims:selectedSolution")}
                disabled={isDisabled}
                data={PreferredResolution}
                value={selectedSolution}
                onChange={(e) => setSelectedSolution(e as PreferredResolutionType)}
                error={errors.preferredResolution ? t("validation:" + errors.preferredResolution) : null}
              />
            )}
            {selectedSolution === ClaimPreferredResolutionEnum.REFUND_PARTIAL && (
              <Group align="top" className={styles.solutionSelect}>
                <Select
                  label={t("claims:selectedSolution")}
                  disabled={isDisabled}
                  data={PreferredResolution}
                  value={selectedSolution}
                  onChange={(e) => setSelectedSolution(e as PreferredResolutionType)}
                  error={errors.preferredResolution ? t("validation:" + errors.preferredResolution) : null}
                />
                <Select
                  label=" "
                  flex={"0 0 30%"}
                  data={refundPercentOptions}
                  value={selectedRefundPercent}
                  onChange={(e) => setSelectedRefundPercent(e as string)}
                  error={errors.refundPercent ? t("validation:" + errors.refundPercent) : null}
                />
              </Group>
            )}
            <Textarea
              label={t("claims:replyToClaim")}
              disabled={isDisabled}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              error={errors.adminNote ? t("validation:" + errors.adminNote) : null}
            />
            <Group>
              <CTA intent="secondary" disabled={isDisabled} loading={isPendingReviewClaim} onClick={handleApproveClaim}>
                {t("actions:approve")}
              </CTA>
              <CTA intent="negate" disabled={isDisabled} loading={isPendingReviewClaim} onClick={handleRejectClaim}>
                {t("actions:reject")}
              </CTA>
            </Group>
          </Stack>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default DetailsTab;
